import React, { useEffect, useRef, useState } from "react";
import { Radio } from "antd";
import AppointmentDatePicker from "./AppointmentDatePicker";
import { getTimeSlots } from "../../apis/calender";
import "./AppointmentCalendar.css";
import { useContext } from "react";
import { AppContext } from "../../Context/AppContext";
import { LoadingSpinner } from "../General/Spinner/index.tsx";
import { isDatefrompast, isTimeRangeOverlap, trackUserInteraction } from "../../Utils/common-utils";
import moment from 'moment-timezone';
import DOMPurify from "dompurify";

const isValidMessage = (message) => {
	if (!message) return false;
	const strippedMessage = message.replace(/<[^>]*>/g, '').trim();
	return strippedMessage.length > 0;
};

const formatPhoneNumber = (message) => {
    const phoneRegex = /(\(?\d{3}\)?)[- ]?\d{3}[- ]\d{4}/g;
    return message.replace(phoneRegex, (match) => {
        const cleaned = match.replace(/\D/g, '');
        return `<a href="tel:${cleaned}" style="text-decoration: underline; display: inline; color: rgb(139, 0, 0);">${match}</a>`;
    });
};

const AppointmentCalendar = () => {
	const { state, updateState } = useContext(AppContext);
	const [calendarData, setCalendarData] = useState(null);
	const [timeSlots, setTimeSlots] = useState([]);
	const [timeLoading, setTimeLoading] = useState(false);
	const [selectedTimeSlot, setSelectedTimeSlot] = useState([]);
	const timeSlotRef = useRef(null);

	const controller = useRef(new AbortController());

	useEffect(() => {
		const currentController = !controller?.current ? new AbortController() : controller?.current;
		trackUserInteraction({
			type:"page",
			page: 'AppointmentCalendar',
			buttonName: 'Select Time Slot',
			buttonValue: 'Select Time Slot',
			timestamp: new Date().toISOString(),
			organization_name: state?.organizationDetails?.organization_name,
			sessionId: state?.clientInfo?.followupleadid,
			step: 7
		}, state);
		
		fetchTimeSlots(currentController);
		return () => {
			currentController.abort();
		};
	}, []);

	
	useEffect(() => {
		if (selectedTimeSlot) {
			updateState({ ...state, isSubStepValid: true });
		}
	}, [selectedTimeSlot]);


	const convertToAMPMWithoutSeconds = (time) => {
		const [hours, minutes] = time.split(":");
		let period = "AM";
		if (parseInt(hours) >= 12) period = "PM";
		const hours12 = parseInt(hours) % 12 || 12;
		return `${hours12}:${minutes} ${period}`;
	};

	const formatTimeRange = (timeArray) => {
		const timeZone = state.organizationDetails?.organization_timeZone || "UTC";
		const isHCP = state.organizationDetails?.organization_provider === "HCP";

		return timeArray?.map((timeObj) => {
			if (!isHCP) {
				return {
					start: convertToAMPMWithoutSeconds(timeObj.start),
					end: convertToAMPMWithoutSeconds(timeObj.end),
					startUtc: timeObj.startUtc,
					endUtc: timeObj.endUtc,
				};
			}
			const startTimeUtc = moment.utc(timeObj.startUtc);
			const endTimeUtc = moment.utc(timeObj.endUtc);
			
			const startTime = startTimeUtc.tz(timeZone).format("h:mm A");
			const endTime = endTimeUtc.tz(timeZone).format("h:mm A");
			
			return {
				start: startTime,
				end: endTime,
				startUtc: timeObj.startUtc,
				endUtc: timeObj.endUtc,
			};
		});
	};

	const checkIfinputDateExist = (option, timeZone) => {
		if (!option?.startUtc) return false;
		const blockDate = moment.tz(option.startUtc, timeZone).startOf('day').format("YYYY-MM-DD");
		return state?.organizationDetails?.holidays?.find((dateInfo) => {
			const formattedHolidayDate = moment(dateInfo.date, "YYYY-MM-D").format("YYYY-MM-DD");
			return formattedHolidayDate === blockDate;
		});
	};

	const handleTimeSlot = (slots) => {
		const formattedTimeSlots = formatTimeRange(slots);
		let updatedSlots = formattedTimeSlots;

		if (!formattedTimeSlots) {
			setTimeSlots(formattedTimeSlots);
			return;
		}
		const timeZone = state.organizationDetails?.organization_timeZone || "UTC";
		const currentDate = moment.tz(timeZone).startOf('day');
		const slotEndDate = moment.tz(formattedTimeSlots[0].startUtc, timeZone).startOf('day');
		const isSameDate = currentDate.isSame(slotEndDate, 'day');
		if (isSameDate) {
			updatedSlots = formattedTimeSlots?.filter((slot) => isDatefrompast(slot, timeZone,state?.organizationDetails?.id)) || [];
		}

		const blockedSlots = updatedSlots?.filter((option) => {
			const blockbyadmin = checkIfinputDateExist(option, timeZone);
			if (!blockbyadmin) return false;
			if (blockbyadmin.BlockAllday) return true;
			const formattedDateRange = `${option?.start} - ${option?.end}`;
			const formattedHolidayDate = moment(blockbyadmin?.date, "YYYY-MM-D").format("YYYY-MM-DD");
			return isTimeRangeOverlap(formattedDateRange, blockbyadmin?.TimeRange, timeZone, formattedHolidayDate);
		});
		const notBlockedSlots = updatedSlots?.filter((option) => !blockedSlots.includes(option));
		setTimeSlots(blockedSlots.length === updatedSlots?.length ? null : notBlockedSlots);
	};

	const fetchTimeSlots = async ({signal}) => {
		try {
			let newState = {
				...state,
				isSubStepValid: false,
				miscellaneous: { ...state.miscellaneous, loading: true, appointmentReviewPage: false },
			}
			updateState({...newState});
			setTimeLoading(true);
			const payload = {
				organizationId: state?.organizationDetails?.id,
				businessUnit: state?.organizationDetails?.organization_provider==='HCP'? (state?.customUrlData?.st_non_member_job_id || state?.additionalInfo?.jobItemToBook?.st_non_member_job_id ): (state?.customUrlData?.buId?.id || state?.additionalInfo?.jobItemToBook?.st_business_unit_id || ""),
			};
			
			const res = await getTimeSlots(payload, signal );
			setCalendarData(res);
		} catch (err) {
			if (signal.aborted) {
				updateState({
					...state,
					isSubStepValid: true,
					miscellaneous: { ...state.miscellaneous, loading: false },
				});
			} else {
				console.error(err);
				updateState({
					...state,
					isSubStepValid: false,
					miscellaneous: { ...state.miscellaneous, loading: false },
				});
			}
		} finally {
			setTimeLoading(false);
		}
	};
	return (
		<div className="appointment-date-picker">
			{timeLoading && (
				<div className="spinner-container">
					<LoadingSpinner />
				</div>
			)}

			<div className="AppointmentCalendar_otr">
				<AppointmentDatePicker
					calendarData={calendarData}
					onSelectedDate={handleTimeSlot}
					setSelectedTimeSlot={setSelectedTimeSlot}
					timeSlotRef={timeSlotRef}
				/>
				<div id="timeSlots" ref={timeSlotRef} className="date_radio_otr">
					{timeSlots ? (
						<>
							<p className="date_text">Please select one of the available time slots</p>
							<div className="date_check_group_wrapper">
								<Radio.Group
									className="radio_check_group"
									onChange={(e) => {
										setSelectedTimeSlot(e.target.value);
										trackUserInteraction({
											type:"button",
											page: 'AppointmentCalendar',
											buttonName: 'Select Time Slot',
											buttonValue: `Last selected time slot ${e?.target?.value?.start} - ${e?.target?.value?.end}`,
											timestamp: new Date().toISOString(),
											userId: state?.userDetails?.id,
											sessionId: state?.clientInfo?.followupleadid,
											organization_name: state?.organizationDetails?.organization_name,
											step: 7
										}, state);
										const day=moment.tz(e?.target?.value?.startUtc, state?.organizationDetails?.timeZone).startOf('day').format("dddd");
										updateState({
											...state,
											appointment: {
												...state.appointment,
												arrivalWindowStart: e?.target.value?.startUtc,
												arrivalWindowEnd: e?.target.value?.endUtc,
												displayStartTime: e?.target?.value?.start,
												displayEndTime: e?.target?.value?.end,
												day,
											},
										});
									}}
									value={selectedTimeSlot}
								>
									{timeSlots?.map((option) => (
										<div className="time_slot_radio_btn_wrapper" key={`${option?.start} - ${option?.end}`}>
											<Radio value={option}>{`${option?.start} - ${option?.end}`}</Radio>
										</div>
									))}
								</Radio.Group>
							</div>
						</>
					) : (
						<span className="noslots_response">
							{isValidMessage(state?.organizationDetails?.noTimeslotsAvailableMessage) ? 
								<div style={{
                                    margin: 0,
                                    color: 'rgb(139, 0, 0)'
                                }} dangerouslySetInnerHTML={{ 
									__html: DOMPurify.sanitize(
                                        formatPhoneNumber(state?.organizationDetails?.noTimeslotsAvailableMessage), 
                                        {
										ALLOWED_TAGS: ['p', 'a', 'br', 'span'],
										ALLOWED_ATTR: ['href', 'target', 'style', 'class'],
										ALLOWED_STYLES: ['color','padding', 'display'],
										ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
										ADD_ATTR: ['target', 'rel'],
										ADD_DATA_URI_TAGS: ['a']
									},{ html: true })
								}} /> 
								: <div style={{
                                    margin: 0,
                                    color: 'rgb(139, 0, 0)'
                                }} dangerouslySetInnerHTML={{ 
                                    __html: DOMPurify.sanitize(
                                        formatPhoneNumber("There are no openings remaining for this date. Please select a different date, or call us if this is an emergency."),
                                        {
                                        ALLOWED_TAGS: ['p', 'a', 'br', 'span'],
                                        ALLOWED_ATTR: ['href', 'target', 'style', 'class'],
                                        ALLOWED_STYLES: ['color','padding', 'display'],
                                        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
                                        ADD_ATTR: ['target', 'rel'],
                                        ADD_DATA_URI_TAGS: ['a']
                                    },{ html: true })
                                }} />}
						</span>
					)}
				</div>
			</div>
		</div>
	);
};

export default AppointmentCalendar;
